import { useState, useRef, useCallback } from "react";
import { useChatRooms } from "./useChatRooms";
import { useChat } from "./useChat";
import { useUnreadTracker } from "./useUnreadTracker";

/**
 * useChatEntry
 *
 * Orquestra o chat da comunidade: resolve as salas do usuário (a do
 * professor, quando existe, + a global, sempre que for aluno), mantém as
 * conexões WS ativas (mesmo com o modal fechado — é o que permite o badge
 * de não-lido reagir a mensagens novas antes do aluno abrir o chat) e
 * controla o estado aberto/fechado do modal + qual aba está ativa. O ponto
 * de montagem (ex: ícone no header do Dashboard) fica livre pra decidir
 * onde/como renderizar o gatilho — este hook só entrega o estado e as ações.
 *
 * Dois "slots" fixos (professor, global) — sempre chamados incondicionalmente
 * (regra dos hooks não permite chamada dinâmica); useChat/useUnreadTracker já
 * não fazem nada com roomId=null, então um slot que não se aplica ao usuário
 * (ex: professor não tem global; self student não tem sala de professor)
 * fica simplesmente ocioso.
 *
 * Feature flag: REACT_APP_CHAT_ENABLED — chat ainda tem fases pendentes
 * (moderação, etc.), então fica atrás dessa variável pra poder ir pra
 * produção desligado e ligar depois sem precisar de outro deploy de código.
 */
const CHAT_FEATURE_ENABLED = process.env.REACT_APP_CHAT_ENABLED === "true";

export function useChatEntry(currentUser) {
  const enabled = Boolean(
    CHAT_FEATURE_ENABLED &&
      currentUser &&
      (currentUser.role === "professor" || currentUser.role === "aluno")
  );

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("professor");

  const { rooms } = useChatRooms(enabled);
  const professorRoom = rooms.find((r) => r.type === "professor") || null;
  const globalRoom = rooms.find((r) => r.type === "global") || null;
  const hasBothRooms = Boolean(professorRoom && globalRoom);
  const effectiveTab = hasBothRooms ? activeTab : professorRoom ? "professor" : "global";

  // Ref "sempre atual" — evita fechar sobre um activeTab obsoleto dentro dos
  // callbacks de mensagem recebida, sem precisar deles como dependência.
  const activeTabRef = useRef(effectiveTab);
  activeTabRef.current = effectiveTab;

  const professorUnread = useUnreadTracker({
    roomId: professorRoom?.id ?? null,
    currentUserId: currentUser?.id,
  });
  const globalUnread = useUnreadTracker({
    roomId: globalRoom?.id ?? null,
    currentUserId: currentUser?.id,
  });

  const handleProfessorIncoming = useCallback(
    (message) => {
      setOpen((currentlyOpen) => {
        if (currentlyOpen && activeTabRef.current === "professor") {
          professorUnread.markAsRead();
        } else {
          const isMention = message.mentions?.some((m) => m.id === currentUser?.id);
          professorUnread.registerIncoming(message.id, isMention);
        }
        return currentlyOpen;
      });
    },
    // professorUnread é um objeto novo a cada render; só as funções desestruturadas
    // (memoizadas via useCallback dentro de useUnreadTracker) importam como dependência estável.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [professorUnread.markAsRead, professorUnread.registerIncoming, currentUser?.id]
  );

  const handleGlobalIncoming = useCallback(
    (message) => {
      setOpen((currentlyOpen) => {
        if (currentlyOpen && activeTabRef.current === "global") {
          globalUnread.markAsRead();
        } else {
          const isMention = message.mentions?.some((m) => m.id === currentUser?.id);
          globalUnread.registerIncoming(message.id, isMention);
        }
        return currentlyOpen;
      });
    },
    // ver comentário em handleProfessorIncoming, acima.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [globalUnread.markAsRead, globalUnread.registerIncoming, currentUser?.id]
  );

  const professorChat = useChat({
    roomId: professorRoom?.id ?? null,
    onIncomingMessage: handleProfessorIncoming,
  });
  const globalChat = useChat({
    roomId: globalRoom?.id ?? null,
    onIncomingMessage: handleGlobalIncoming,
  });

  const handleSetActiveTab = useCallback(
    (tab) => {
      setActiveTab(tab);
      (tab === "professor" ? professorUnread : globalUnread).markAsRead();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ver comentário em handleProfessorIncoming
    [professorUnread.markAsRead, globalUnread.markAsRead]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    (effectiveTab === "professor" ? professorUnread : globalUnread).markAsRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ver comentário em handleProfessorIncoming
  }, [effectiveTab, professorUnread.markAsRead, globalUnread.markAsRead]);

  const handleClose = useCallback(() => setOpen(false), []);

  return {
    enabled,
    open,
    handleOpen,
    handleClose,
    unreadCount: professorUnread.unreadCount + globalUnread.unreadCount,
    hasMentionUnread: professorUnread.hasMentionUnread || globalUnread.hasMentionUnread,
    showTabs: hasBothRooms,
    activeTab: effectiveTab,
    onTabChange: handleSetActiveTab,
    // roomId da aba ativa - o composer (ChatModal) precisa disso pra buscar
    // candidatos a @menção em /chat/rooms/{id}/members.
    roomId: (effectiveTab === "professor" ? professorRoom : globalRoom)?.id ?? null,
    chat: effectiveTab === "professor" ? professorChat : globalChat,
  };
}
